"use client";

import type { ReactNode } from "react";
import Link from "next/link";
import PageContent from "@/components/page-content";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

type Tier = {
  id: string;
  name: string;
  summary: ReactNode;
  price: string;
  cadence?: ReactNode;
  footnote?: ReactNode;
  badge?: string;
  highlight?: boolean;
  sections?: Array<{
    title: string;
    items: ReactNode[];
  }>;
  features?: ReactNode[];
  note?: {
    title: string;
    body: ReactNode;
  };
  cta: {
    href?: string;
    label: string;
    variant?: "default" | "outline" | "secondary";
    type?: "dialog";
  };
  dialog?: never;
};

const tiers: Tier[] = [
  {
    id: "standard",
    name: "Standard Plan",
    summary: <em>Pay-as-you-go for builders and teams.</em>,
    price: "Usage-based",
    cadence: "Billed by tokens used (input and output counted separately)",
    sections: [
      {
        title: "Price",
        items: [
          "$0.30 per 1,000,000 input tokens",
          "$2.50 per 1,000,000 output tokens",
        ],
      },
      {
        title: "What you get",
        items: [
          "Usage-based billing. Input and output are counted separately and billed per token.",
          "$5 in free credits every month to jumpstart experiments.",
          "Dense grounding tokens. Do more with fewer tokens for grounding-heavy workloads.",
          "Start instantly. No sales call required.",
          "Privacy first. We never train on your data.",
        ],
      },
      {
        title: "Validity & refunds",
        items: [
          "Prepaid tokens are valid for 12 months.",
          "Unused tokens expire after one year and are non-refundable (except where required by law).",
        ],
      },
    ],
    cta: {
      href: "/c/cloud/api-keys",
      label: "Get API key",
    },
  },
  {
    id: "enterprise",
    name: "Enterprise Plan",
    summary: <em>Tailored for regulated and high-scale workloads.</em>,
    price: "Custom Pricing",
    sections: [
      {
        title: "What you get",
        items: [
          (
            <>
              <strong>Custom pricing &amp; terms.</strong> Volume, commit, or bespoke
              commercial agreements.
            </>
          ),
          (
            <>
              <strong>Compliance options.</strong> HIPAA support for covered
              workloads.
            </>
          ),
          (
            <>
              <strong>Deployment flexibility.</strong> Run in your own environment
              (on-prem or VPC) for full data control.
            </>
          ),
          (
            <>
              <strong>Performance at scale.</strong> Higher/dedicated throughput and
              capacity, aligned to your SLA needs.
            </>
          ),
          (
            <>
              <strong>Dedicated support.</strong> Priority channel with <strong>24/7</strong>{" "}
              incident response.
            </>
          ),
          (
            <>
              <strong>Data controls.</strong> No training on your data; enterprise
              retention and governance options.
            </>
          ),
        ],
      },
    ],
    cta: {
      label: "Talk to sales",
      variant: "outline",
    },
  },
];

export function PricingPlans() {
  return (
    <PageContent className="pt-0">
      <div className="flex flex-col items-center gap-10">
        <div className="grid w-full gap-6 md:grid-cols-2">
          {tiers.map((tier) => (
            <Card
              key={tier.id}
              className={cn(
                "relative flex h-full flex-col border-border",
                tier.highlight && "border-primary shadow-lg"
              )}
            >
              {tier.badge && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-xs font-semibold">
                  {tier.badge}
                </Badge>
              )}

              <CardHeader className="space-y-4 pb-4">
                <CardTitle className="text-2xl font-semibold">{tier.name}</CardTitle>
                <p className="text-base text-muted-foreground">{tier.summary}</p>
                <div className="flex flex-col items-start gap-1">
                  <span className="text-3xl font-semibold tracking-tight">
                    {tier.price}
                  </span>
                  {tier.cadence && (
                    <span className="text-base font-medium text-muted-foreground">
                      {tier.cadence}
                    </span>
                  )}
                  {tier.footnote && (
                    <span className="text-base text-muted-foreground">{tier.footnote}</span>
                  )}
                </div>
              </CardHeader>

              <CardContent className="flex flex-1 flex-col gap-6">
                {tier.sections ? (
                  <div className="space-y-6 text-left text-base text-muted-foreground">
                    {tier.sections.map((section) => (
                      <div key={`${tier.id}-${section.title}`} className="space-y-3">
                        <p className="font-semibold text-foreground">{section.title}</p>
                        <ul className="space-y-3">
                          {section.items.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <Check className="mt-0.5 size-4 flex-shrink-0 text-primary" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : (
                  <ul className="space-y-3 text-left text-base text-muted-foreground">
                    {tier.features?.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className="mt-0.5 size-4 flex-shrink-0 text-primary" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-auto flex flex-col gap-3">
                  {tier.id === "enterprise" ? (
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <Button asChild size="lg" className="flex-1">
                        <a
                          href="https://cal.com/moondream-sales"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Schedule a call
                        </a>
                      </Button>
                      <Button asChild size="lg" variant="outline" className="flex-1">
                        <a href="mailto:sales@moondream.ai">Email sales</a>
                      </Button>
                    </div>
                  ) : (
                    <Button
                      asChild
                      size="lg"
                      variant={tier.cta.variant ?? (tier.highlight ? "default" : "outline")}
                      className="w-full"
                    >
                      <Link href={tier.cta.href ?? "#"}>{tier.cta.label}</Link>
                    </Button>
                  )}
                </div>

              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </PageContent>
  );
}
